import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { showDialog } from "@widgets/CommonDialog";
import { fnSave, fnGetTableById } from "@shared/api/restApi";
import type { ExcelUpload_S02_Params } from "@menu/XTC/SN_SAL_SAO_REG_001_TEX/excel-upload-tex/types";
import { global, globalMenu } from "@shared/libs/Global";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import { dateToString, formatYyyyMMdd } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";
import {
  getTablebySql,
  getInitFormData,
  CalAm,
} from "@shared/utils/CommonUtils";

interface ChangeSet {
  modifiedRowcount: number;
  insert: any[];
  update: any[];
  delete: any[];
}
interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

type S02WithModalProps = ExcelUpload_S02_Params & {
  modalRef?: React.RefObject<any>;
};

/**
 * S02 (심플/안정)
 * - S01 validRows를 컬럼정의대로 그대로 주입
 * - effGrouping으로 묶어 전표번호 채번, 전표별 line 1..N
 * - 모든 행 state='insert' 마킹 → getChange()가 insert로 잡힘
 * - 저장: getChange() 그대로 tables.header/detail 키로 전송
 */

const ExcelUpload_S02_ReviewSave = forwardRef<any, S02WithModalProps>(
  ({ modalRef, ...props }, ref) => {
    const {
      validRows,
      headerColumns,
      detailColumns,
      grouping,
      controller,
      numbering,
      tables,
      fieldMap, // { headerNoField, detailNoField, lineNoField } (필수)
    } = props;
    const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      grid1: React.createRef(),
      grid2: React.createRef(),
    });
    // === 필수 필드명 ===
    const headerNoField = fieldMap?.headerNoField!;
    const detailNoField = fieldMap?.detailNoField!;
    const lineNoField = fieldMap?.lineNoField!;
    const today = new Date();
    const [popupParent, setPopupParent] = useState<any>(null);
    const todayform = `${today.getFullYear()}${(today.getMonth() + 1)
      .toString()
      .padStart(2, "0")}${today.getDate().toString().padStart(2, "0")}`;
    const { userInfo } = global();
    const [columns, setColums] = useState<any>({
      grid1: [],
      grid2: [],
    });
    const [dataSources, setDataSources] = useState<any>({
      grid1: [],
      grid2: [],
    });
    const syncingFromHeaderRef = useRef(false);
    // === 컬럼 필드 목록 (useMemo로 고정) ===
    const headerFields = useMemo(
      () =>
        (headerColumns ?? [])
          .map((c: any) => String(c?.field ?? ""))
          .filter((f) => f.length > 0),
      [headerColumns]
    );
    const detailFields = useMemo(
      () =>
        (detailColumns ?? [])
          .map((c: any) => String(c?.field ?? ""))
          .filter((f) => f.length > 0),
      [detailColumns]
    );

    // === 유효 그룹핑: 시스템 키 제외 (useMemo 고정) ===
    const effGrouping = useMemo(
      () =>
        (grouping ?? []).filter(
          (f) =>
            !!f &&
            f !== headerNoField &&
            f !== detailNoField &&
            f !== lineNoField
        ),
      [grouping, headerNoField, detailNoField, lineNoField]
    );

    // === 헬퍼 ===
    const pick = (row: any, fields: string[]) => {
      const out: any = {};
      for (const f of fields) out[f] = row?.[f];
      return out;
    };
    const makeKey = (row: any) =>
      effGrouping.map((f) => row?.[f] ?? "").join("||");

    // numbering 안정화 (의존성 최소화)
    const numberingFn = numbering.fn;
    const { cd_module, no_line_tex, cd_prefix, cd_menu, dateField } =
      numbering.options;

    // === 그리드 refs / state ===
    const [headerRows, setHeaderRows] = useState<any[]>([]);
    const [detailRows, setDetailRows] = useState<any[]>([]);
    const didMarkRef = useRef(false);

    // === 그룹핑 (useMemo) ===
    const groups = useMemo(() => {
      const map = new Map<string, any[]>();
      for (const r of validRows) {
        const k = makeKey(r);
        if (!map.has(k)) map.set(k, []);
        map.get(k)!.push(r);
      }
      return map;
    }, [validRows, effGrouping]);
    useEffect(() => {
      if (modalRef?.current) {
        setPopupParent(modalRef.current.getPopupParent());
      }
    }, [modalRef?.current]);
    // === 데이터 구성: 헤더/디테일 ===
    useEffect(() => {
      let cancelled = false;
      setColums({
        grid1: headerColumns,
        grid2: detailColumns,
      });
      (async () => {
        didMarkRef.current = false;

        // 필수 필드 누락 가드
        if (!headerNoField || !detailNoField || !lineNoField) {
          setHeaderRows([]);
          setDetailRows([]);
          await showDialog(
            "alert",
            "fieldMap(headerNoField/detailNoField/lineNoField)이 필요합니다."
          );
          return;
        }

        const H: any[] = [];
        const L: any[] = [];

        for (const [, rows] of Array.from(groups.entries())) {
          const sample = rows[0] ?? {};

          // 전표 채번 (dateField의 "값"을 넣어서 호출)
          const slipNo = await numberingFn(
            cd_module,
            no_line_tex,
            cd_prefix,
            sample?.[dateField],
            cd_menu
          );
          if (cancelled) return;

          // 헤더 1행: headerColumns.field 기준으로 "있는 그대로" 채우고 slipNo 주입
          const hdr = { ...sample, [headerNoField]: slipNo };
          H.push(hdr);

          // 디테일: rows 각각을 detailColumns.field 기준으로 채우고 slipNo/line 부여
          let seq = 0;
          for (const r of rows) {
            seq += 1;
            const d: any = {
              ...r,
              [detailNoField]: slipNo,
              [lineNoField]: seq,
            };

            L.push(d);
          }
        }
        if (cancelled) return;

        // 그리드 비우고
        await gridRefs.current["grid1"].current?.deleteAllRows();
        await gridRefs.current["grid2"].current?.deleteAllRows();
        const sqlQuery = {
          sql: `select a.*,b.dc_ref
          from(
            select cd_so,fg_trns,tp_sales,yn_trade,yn_app,cd_firm,
            case when fg_trns = '1' then '11'
            when fg_trns = '2' or fg_trns = '3' then '14'
            else '15' end as fg_vat
            from mas_sotype 
            where cd_firm = :cd_firm and yn_use = 'Y' and cd_so = '5100'
            ) a left outer join mas_codel b on a.cd_firm = b.cd_firm
            where b.cd_firm = :cd_firm
            and b.cd_clas = 'SYS013'
            and b.cd_flag = a.fg_vat ;`,
          cd_firm: userInfo.cd_firm,
        };
        const rows = await getTablebySql(sqlQuery);
        const findExistingData = rows?.[0];
        for (const row of L) {
          row.vat_late = findExistingData?.dc_ref;
        }
        await calcAmountsForRows(headerColumns, L, H);

        // 같은 전표 안에서 no_po, no_style, cd_dummy01, cd_var1~3 이 같은 행은 한 줄로 묶는다
        // 수량·금액은 합치고, 단가 등 나머지 값은 첫 행 기준
        const sumFields = ["qt", "am", "am_k", "am_vat", "am_supply"];
        const mergedMap = new Map<string, any>();
        for (const row of L) {
          const k = [
            row[detailNoField],
            row.no_po,
            row.no_style,
            row.cd_dummy01,
            row.cd_var1,
            row.cd_var2,
            row.cd_var3,
          ]
            .map((v) => v ?? "")
            .join("||");
          const dst = mergedMap.get(k);
          if (!dst) {
            mergedMap.set(k, { ...row });
            continue;
          }
          for (const f of sumFields) {
            dst[f] = (Number(dst[f]) || 0) + (Number(row[f]) || 0);
          }
        }
        // 묶은 뒤 전표별 line 번호 다시 1..N
        const lineSeq = new Map<string, number>();
        const mergedL = Array.from(mergedMap.values()).map((row) => {
          const seq = (lineSeq.get(row[detailNoField]) ?? 0) + 1;
          lineSeq.set(row[detailNoField], seq);
          return { ...row, [lineNoField]: seq };
        });
        L.splice(0, L.length, ...mergedL);

        const newHeaders = H.map((hdr) => ({
          ...hdr,
          cd_firm: userInfo?.cd_firm,
          cd_biz: userInfo?.cd_biz,
          cd_dept: userInfo?.cd_dept,
          cd_emp: userInfo?.cd_emp,
          nm_emp: userInfo?.nm_emp,
          cd_gp: userInfo?.cd_gp,
          cd_so: findExistingData.cd_so,
          fg_trns: findExistingData.fg_trns,
          vat_late: findExistingData.dc_ref,
          fg_money: "1",
          tp_um: "001",
          cd_exch: "000",
          rt_exch: 1,
          fg_vat:
            findExistingData.fg_trns === "1"
              ? "11"
              : findExistingData.fg_trns === "2" ||
                  findExistingData.fg_trns === "3"
                ? "14"
                : "15",
          tp_sales: findExistingData.tp_sales,
          yn_trade: findExistingData.yn_trade,
          st_so: findExistingData.yn_app === "Y" ? "R" : "F",
        }));
        // 헤더 추가 (=> modifiedRows.added에 자동 기록)
        for (const hdr of newHeaders) {
          gridRefs.current["grid1"].current?.addRow(hdr);
        }

        // 디테일 추가 (=> modifiedRows.added에 자동 기록)
        for (const row of L) {
          const sqlQuery2 = {
            sql: `select nm_item from mas_item where cd_firm = :cd_firm and cd_item = :cd_item`,
            cd_firm: userInfo.cd_firm,
            cd_item: row.no_style.concat(row.cd_var1, row.cd_var2),
          };
          const rows = await getTablebySql(sqlQuery2);
          row.cd_firm = userInfo?.cd_firm;
          row.cd_item = row.no_style.concat(row.cd_var1, row.cd_var2);
          row.nm_item = rows?.[0]?.nm_item ?? "";
          row.fg_vat = newHeaders[0].fg_vat;
          row.vat_late = newHeaders[0].vat_late;
          row.dt_dlv = newHeaders[0].dt_dlv;
          gridRefs.current["grid2"].current?.addRow(row);
        }

        // 첫 전표 기준으로 디테일 필터/포커스
        if (H.length > 0) {
          const slip = H[0][headerNoField];
          gridRefs.current["grid1"].current?.selectAndFocusRow(0);
          gridRefs.current["grid2"].current?.applyFilters({
            [detailNoField]: slip,
          });
          gridRefs.current["grid2"].current?.selectAndFocusRow(0);
        }

        // 렌더 후 포커스/필터 + 모든 행 state='insert' 마킹
        setTimeout(() => {
          if (H.length > 0) {
            gridRefs.current["grid1"].current?.selectAndFocusRow(0);
            const slip = H[0]?.[headerNoField];
            if (slip) {
              gridRefs.current["grid2"].current?.applyFilters({
                [detailNoField]: slip,
              });
              gridRefs.current["grid2"].current?.selectAndFocusRow(0);
            }
          }
        }, 0);
      })();
      return () => {
        cancelled = true;
      };
    }, [
      groups, // validRows/effGrouping 바뀔 때만 변경
      headerFields, // useMemo로 고정
      detailFields, // useMemo로 고정
      headerNoField,
      detailNoField,
      lineNoField,
      numberingFn, // 함수 레퍼런스
      cd_module,
      no_line_tex,
      cd_prefix,
      cd_menu,
      dateField, // 원시값
    ]);

    // === 상단 포커스 → 하단 필터 ===
    const onFocusedRowChanged = (row: any) => {
      if (!row) return;
      const slip = row?.[headerNoField];
      gridRefs.current["grid2"].current?.applyFilters({
        [detailNoField]: slip,
      });
      gridRefs.current["grid2"].current?.selectAndFocusRow(0);
    };

    // === 저장 ===
    const onSave = async (): Promise<boolean> => {
      const soh = gridRefs.current["grid1"].current?.getChange();
      for (const row of soh.insert) {
        if (
          (row.no_po.length > 0 && row.no_po.length <= 2) ||
          !/^\d{3}$/.test(row.no_po.substring(0, 3))
        ) {
          await showDialog("alert", "PO 형식이 맞지 않습니다.");
          return false;
        }
      }
      const sol_tex = gridRefs.current["grid2"].current?.getChange();
      const requiredValuesCheckH =
        gridRefs.current["grid1"].current?.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      // Validate required fields (Line)
      const requiredValuesCheckL =
        gridRefs.current["grid2"].current?.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;
      if (
        (soh?.modifiedRowcount ?? 0) === 0 &&
        (sol_tex?.modifiedRowcount ?? 0) === 0
      ) {
        await showDialog("alert", "변경된 내용이 없습니다.");
        return false;
      }

      const targetRows = [...(soh?.insert ?? []), ...(soh?.update ?? [])];

      // ✅ no_po 값만 추출 + 중복 제거
      const noPoList = Array.from(
        new Set(
          targetRows
            .map((row: any) => row.no_po)
            .filter((no_po: string) => no_po && no_po !== "")
        )
      );

      // ✅ 중복 체크
      for (const no_po of noPoList) {
        const payload = {
          id: "duplication_no_po",
          params: {
            cd_firm: userInfo.cd_firm,
            no_po,
          },
        };

        const duplication = await fnGetTableById(payload);

        if (duplication.length > 0) {
          const confirm = await showDialog(
            "confirm",
            "수주일 " +
              formatYyyyMMdd(duplication[0].dt_so) +
              " 기준,\n중복된 PO번호(" +
              no_po +
              ")가 존재합니다.\n저장하시겠습니까?"
          );

          if (!confirm) return false;
        }
      }

      ["insert", "update"].forEach((key) => {
        soh[key] = soh[key]?.map((row: any) => ({
          ...row,
          cd_cust_dlv: row.cd_cust ?? row.cd_cust_dlv,
        }));
      });
      ["insert", "update"].forEach((key) => {
        sol_tex[key] = sol_tex[key]?.map((row: any) => ({
          ...row,
          cd_firm: userInfo?.cd_firm,
          cd_item: row.no_style.concat(row.cd_var1).concat(row.cd_var2),
          st_so: soh.insert[0].st_so,
          fg_sales: soh.insert[0].fg_sales,
          yn_trade: soh.insert[0].yn_trade,
          dt_dlv: soh.insert[0].dt_dlv,
        }));
      });
      const sol = buildSalSolChanges(sol_tex);
      const payload: Record<string, any> = {
        [String(tables.header)]: soh,
        [String(tables.detail)]: sol_tex,
        [String(tables.blind)]: sol,
      };
      let rtn: boolean = false;
      rtn = await fnSave(controller, payload);
      if (rtn) {
        gridRefs.current["grid1"].current?.resetUpdate();
        gridRefs.current["grid2"].current?.resetUpdate();

        return true;
      }

      return false;
    };

    const buildSalSolChanges = (salSolTexChanges: ChangeSet) => {
      const salSolChanges: ChangeSet = {
        modifiedRowcount: 0,
        insert: [],
        update: [],
        delete: [],
      };

      const numericFields = [
        "qt",
        "am",
        "am_k",
        "am_vat",
        "am_supply",
      ] as const;
      const toNum = (v: any) =>
        typeof v === "number"
          ? v
          : Number(String(v ?? "").replace(/,/g, "")) || 0;

      const addSum = (dst: any, src: any) => {
        ["cd_firm", "no_so", "cd_item", "no_line", "cd_dummy01"].forEach(
          (f) => {
            dst[f] = src[f];
          }
        );
        numericFields.forEach((f) => {
          dst[f] = toNum(dst[f]) + toNum(src[f]);
        });
        return dst;
      };

      const updateDataCheck = (dst: any) => {
        numericFields.forEach((f) => {
          dst[f] = toNum(dst[f]);
        });
      };

      const keyOf = (r: any) =>
        `${r.no_so}||${r.cd_item}||${r.no_line}||${r.cd_dummy01}`;

      const pendingInsert = new Map<string, any>();
      const pendingUpdate = new Map<string, any>();

      (["insert", "update", "delete"] as const).forEach((key) => {
        (salSolTexChanges[key] || []).forEach((row: any) => {
          switch (key) {
            case "insert":
              const k = keyOf(row);
              if (pendingInsert.has(k)) {
                addSum(pendingInsert.get(k), row);
              } else {
                pendingInsert.set(k, { ...row });
              }
              break;

            case "update":
              const ku = keyOf(row);
              if (pendingUpdate.has(ku)) {
                addSum(pendingUpdate.get(ku), row);
              } else {
                pendingUpdate.set(ku, updateDataCheck({ ...row }));
              }
              break;

            case "delete":
              const kd = keyOf(row);
              // 👉 여기서는 간단히 처리 (필요 시 기존 로직 복붙)
              pendingUpdate.set(kd, updateDataCheck({ ...row }));
              break;
          }
        });
      });

      salSolChanges.insert = Array.from(pendingInsert.values());
      salSolChanges.update = Array.from(pendingUpdate.values());
      salSolChanges.modifiedRowcount =
        salSolChanges.insert.length +
        salSolChanges.update.length +
        salSolChanges.delete.length;
      const processedRows = assignNoLineForExcelUpload(
        salSolChanges,
        salSolTexChanges
      );
      salSolChanges.insert = processedRows;
      return salSolChanges;
    };
    const assignNoLineForExcelUpload = (
      salSolChanges: ChangeSet,
      salSolTexChanges: ChangeSet
    ) => {
      // 헤더 그룹핑 키 만드는 함수
      const headerKey = (row: any) =>
        `${row.no_so}||${row.dt_so}||${row.cd_cust}||${row.cd_so}||${row.cd_exch}||${row.rt_exch}||${row.tp_um}||${row.cd_gp}||${row.cd_emp}||${row.cd_cust_dlv}`;

      // 헤더 그룹핑
      const groupedByHeader: Record<string, any[]> = {};
      for (const row of [...salSolChanges.insert, ...salSolChanges.update]) {
        const key = headerKey(row);
        if (!groupedByHeader[key]) groupedByHeader[key] = [];
        groupedByHeader[key].push(row);
      }

      // 👉 cd_item → no_line 매핑 테이블
      const itemLineMap: Record<string, number> = {};

      // 각 헤더 그룹 안에서 no_line 부여
      for (const header in groupedByHeader) {
        const rows = groupedByHeader[header];

        // 아이템별 그룹핑
        const groupedByItem: Record<string, any[]> = {};
        for (const row of rows) {
          const itemKey = `${row.cd_item}||${row.cd_dummy01}`;
          if (!groupedByItem[itemKey]) groupedByItem[itemKey] = [];
          groupedByItem[itemKey].push(row);
        }

        // 아이템별로 no_line 순차 부여
        let lineNo = 1;
        for (const cd_item in groupedByItem) {
          const itemRows = groupedByItem[cd_item];
          for (const r of itemRows) {
            r.no_line = lineNo; // 같은 cd_item은 같은 번호
            itemLineMap[`${r.cd_item}||${r.cd_dummy01}`] = lineNo; // 👈 동시에 map에 저장
          }
          lineNo++; // 다음 아이템은 다음 번호
        }
      }

      // 👉 salSolTexChanges에도 동일한 no_line_so 부여
      for (const row of [
        ...salSolTexChanges.insert,
        ...salSolTexChanges.update,
      ]) {
        const lineSo = itemLineMap[`${row.cd_item}||${row.cd_dummy01}`];
        if (lineSo != null) {
          row.no_line_so = lineSo;
        } else {
          // cd_item이 salSol에 없을 경우 fallback (옵션)
          row.no_line_so = row.no_line_tex;
        }
      }

      return salSolChanges.insert;
    };
    const onCellValueChangedH = async (event: CellValueChangedEvent) => {
      if (!event) return;
      const gridApiH = gridRefs.current["grid1"].current;
      const colId = event.colId;
      const rowData = event.rowData;
      const rowNode = event.rowNode;
      if (gridApiH) {
        const selectedDataH =
          gridRefs.current["grid1"].current.getSelectedRows()[0];
        if (selectedDataH.length > 0) {
          selectedDataH.forEach((row: any) => {
            if (row.cd_cust !== row._previousCdCust) {
              row.cd_cust_dlv = row.cd_cust; // cd_cust_dlv에 값을 넣음
            }
          });
        }
      }
      if (colId === "rt_exch") {
        syncingFromHeaderRef.current = true;
        let totalAm = 0,
          totalUm = 0,
          totalAm_k = 0,
          totalAm_vat = 0,
          totalAm_supply = 0;
        gridRefs.current["grid2"].current.forEachFilteredNode(
          (node: RowNode) => {
            const rt_exch = Number(rowData.rt_exch ?? 1);
            const qt = Number(node.data.qt ?? 0);
            const um = Number(node.data.um ?? 0);

            const newAm = qt * um;
            const newAmK = newAm * rt_exch;
            const newAmVat = Math.floor((newAmK * rowData.vat_late) / 100);
            const newAmSupply = newAmK + newAmVat;
            const newUmK = um * rt_exch;

            node.setDataValue("am", newAm);
            node.setDataValue("am_k", newAmK);
            node.setDataValue("am_vat", newAmVat);
            node.setDataValue("am_supply", newAmSupply);
            node.setDataValue("um_k", newUmK);
            node.setDataValue(
              "um_supply",
              newUmK + Math.floor((newUmK * rowData.vat_late) / 100)
            );
            totalAm += newAm;
            totalUm += um;
            totalAm_k += newAmK;
            totalAm_vat += newAmVat;
            totalAm_supply += newAmSupply;
            /*
            node.data.am = newAm;
            node.data.am_k = newAmK;
            node.data.am_vat = newAmVat;
            node.data.am_supply = newAmSupply;
            node.data.um_k = newUmK;
            node.data.um_supply =
              newUmK + Math.floor((newUmK * rowData.vat_late) / 100);
              */
          }
        );
        // const footerData = gridRefs.current["grid2"].current.getFooterData();
        rowNode.setDataValue("am", totalAm);
        rowNode.setDataValue("um", totalUm);
        rowNode.setDataValue("am_k", totalAm_k);
        rowNode.setDataValue("am_vat", totalAm_vat);
        rowNode.setDataValue("am_supply", totalAm_supply);
        setTimeout(() => {
          syncingFromHeaderRef.current = false;
        }, 0);
      } else if (colId === "no_so") {
        // 필터링
        gridRefs.current["grid2"].current.applyFilters({
          no_so: rowData.no_so,
        });
      } else if (colId === "cd_cust") {
        rowNode.setDataValue("cd_cust_dlv", rowData.cd_cust);
        rowNode.setDataValue("nm_cust_dlv", rowData.nm_cust_sort);
      } else if (colId === "cd_so") {
        syncingFromHeaderRef.current = true;
        const sqlQuery = {
          sql: `select a.*, b.dc_ref
                from(
                    select fg_trns,tp_sales,yn_trade,yn_app,cd_firm,
                           case when fg_trns = '1' then '11'
                                when fg_trns = '2' or fg_trns = '3' then '14'
	                              else '15' end as fg_vat
                    from mas_sotype where cd_firm = :cd_firm and cd_so = :cd_so
                ) a left outer join mas_codel b on a.cd_firm = b.cd_firm
                where b.cd_firm = :cd_firm
                  and b.cd_clas = 'SYS013'
                  and b.cd_flag = a.fg_vat`,
          cd_firm: userInfo.cd_firm,
          cd_so: rowData.cd_so,
        };

        const rows = await getTablebySql(sqlQuery);
        rowNode.setDataValue("fg_trns", rows[0].fg_trns);
        let fg_vat = "";
        if (rows[0].fg_trns === "1") {
          fg_vat = "11";
        } else if (rows[0].fg_trns === "2" || rows[0].fg_trns === "3") {
          fg_vat = "14";
        } else {
          fg_vat = "15";
        }
        rowNode.setDataValue("fg_vat", fg_vat);
        rowNode.setDataValue("vat_late", rows[0].dc_ref);
        let totalAm = 0,
          totalUm = 0,
          totalAm_k = 0,
          totalAm_vat = 0,
          totalAm_supply = 0;
        gridRefs.current["grid2"].current.forEachFilteredNode(
          (node: RowNode) => {
            const rt_exch = Number(rowData.rt_exch ?? 1);
            const qt = Number(node.data.qt ?? 0);
            const um = Number(node.data.um ?? 0);

            const newAm = qt * um;
            const newAmK = newAm * rt_exch;
            const newAmVat = Math.floor((newAmK * rowData.vat_late) / 100);
            const newAmSupply = newAmK + newAmVat;
            const newUmK = um * rt_exch;
            totalAm += newAm;
            totalUm += um;
            totalAm_k += newAmK;
            totalAm_vat += newAmVat;
            totalAm_supply += newAmSupply;
            node.setDataValue("am", newAm);
            node.setDataValue("am_k", newAmK);
            node.setDataValue("am_vat", newAmVat);
            node.setDataValue("am_supply", newAmSupply);
            node.setDataValue("um_k", newUmK);
            node.setDataValue(
              "um_supply",
              newUmK + Math.floor((newUmK * rowData.vat_late) / 100)
            );

            /*
            node.data.am = newAm;
            node.data.am_k = newAmK;
            node.data.am_vat = newAmVat;
            node.data.am_supply = newAmSupply;
            node.data.um_k = newUmK;
            node.data.um_supply =
              newUmK + Math.floor((newUmK * rowData.vat_late) / 100);
            node.data.fg_vat = fg_vat;
            node.data.vat_late = rows[0].dc_ref;
            */
            node.setDataValue("fg_vat", fg_vat);
            node.setDataValue("vat_late", rows[0].dc_ref);
          }
        );
        setTimeout(() => {
          syncingFromHeaderRef.current = false;
        }, 0);
        rowNode.setDataValue("am", totalAm);
        rowNode.setDataValue("um", totalUm);
        rowNode.setDataValue("am_k", totalAm_k);
        rowNode.setDataValue("am_vat", totalAm_vat);
        rowNode.setDataValue("am_supply", totalAm_supply);
        rowNode.setDataValue("tp_sales", rows[0].tp_sales);
        rowNode.setDataValue("yn_trade", rows[0].yn_trade);
        rowNode.setDataValue("st_so", rows[0].yn_app === "Y" ? "R" : "F");
      } else if (colId === "no_po") {
        const noPo = String(rowData.no_po ?? "").trim();
        if (noPo.length <= 2 || !/^\d{3}$/.test(noPo.substring(0, 3))) {
          await showDialog("alert", "PO 형식이 맞지 않습니다.");
          rowNode.setDataValue("no_po", "", "noEvent");
          return;
        }
      }
    };
    const onCellValueChangedL = async (event: CellValueChangedEvent) => {
      if (!event) return;
      const colId = event.colId;
      const rowData = event.rowData;
      if (syncingFromHeaderRef.current) {
        return;
      }
      const focusedCellH = gridRefs.current["grid1"].current.focusedRowNode();
      // 수정된 컬럼 ID 및 정보
      calculateAmounts(focusedCellH, rowData, colId);

      // gridApiRefL에서 footerData 가져오기
      // await setFooterDataToRow();
    };

    const calculateAmounts = async (
      rowHeader: any,
      row: any,
      fieldName: string
    ) => {
      if (!row || !fieldName) {
        return;
      }
      const cleanNumber = (v: any) => {
        if (v == null) return 0;
        return Number(String(v).replace(/[^0-9.-]/g, "")) || 0;
      };

      // 헤더에서 환율(rt_exch) 추출 (없으면 1로 기본)
      const rt_exch = cleanNumber(
        headerColumns.find((h) => h.nm_column === "rt_exch")?.value ?? 1
      );
      const qt = Number(cleanNumber(row.qt) ?? 0);
      const um = Number(cleanNumber(row.um) ?? 0);
      const am = Number(row.am ?? 0);
      const am_k = Number(row.am_k ?? 0);
      const vat_late = Number(row.vat_late ?? 0);

      switch (fieldName) {
        case "qt":
        case "um":
        case "rt_exch": {
          const newAm = qt * um;
          const newAmK = newAm * rt_exch;
          const newAmVat = Math.floor((newAmK * vat_late) / 100);
          const newAmSupply = newAmK + newAmVat;

          row.am = newAm;
          row.am_k = newAmK;
          row.am_vat = newAmVat;
          row.am_supply = newAmSupply;
          row.um_k = um * rt_exch;
          //row.um_supply = newAmSupply / (qt === 0 ? 1 : qt);
          row.um_supply = Math.floor((row.um_k * vat_late) / 100) + row.um_k;
          break;
        }

        case "am": {
          const newAmK = am * rt_exch;
          row.am_k = newAmK;
          row.am_vat = Math.floor((newAmK * vat_late) / 100);
          row.am_supply = newAmK + row.am_vat;
          break;
        }

        case "am_k": {
          row.am = am_k / rt_exch;
          row.am_vat = Math.floor((am_k * vat_late) / 100);
          row.am_supply = am_k + Math.floor((am_k * vat_late) / 100);
          break;
        }

        case "am_vat": {
          row.am_supply = am_k + Math.floor((am_k * vat_late) / 100);
          break;
        }
      }
    };

    const calcAmountsForRows = async (
      headerColumns: any[],
      L: any[],
      H: any[]
    ) => {
      // 숫자만 추출하는 유틸: $, , 등 모든 비숫자 문자 제거
      const cleanNumber = (v: any) => {
        if (v == null) return 0;
        return Number(String(v).replace(/[^0-9.-]/g, "")) || 0;
      };

      // 헤더에서 환율(rt_exch) 추출 (없으면 1로 기본)
      const rt_exch = cleanNumber(
        headerColumns.find((h) => h.nm_column === "rt_exch")?.value ?? 1
      );
      const keyOf = (row: any) =>
        [
          row.no_so,
          row.dt_so,
          row.cd_cust,
          row.no_po,
          row.cd_so,
          row.cd_exch,
          row.rt_exch,
          row.tp_um,
          row.cd_gp,
          row.cd_emp,
          row.cd_cust_dlv,
        ].join("|");
      const groupMap = new Map<string, any>();
      for (let i = 0; i < L.length; i++) {
        const row = L[i];

        // 금액, 수량 등의 값에 $19.57, "1,000" 등 다양한 형태가 올 수 있음
        const qt = cleanNumber(row.qt);
        const um = cleanNumber(row.um);

        const newAm = qt * um;
        const newAmK = newAm * rt_exch;
        const newAmVat =
          isNaN(Math.floor((newAmK * row.vat_late) / 100)) === true
            ? 0
            : Math.floor((newAmK * row.vat_late) / 100);
        const um_vat =
          isNaN(Math.floor((um * rt_exch * row.vat_late) / 100)) === true
            ? 0
            : Math.floor((um * rt_exch * row.vat_late) / 100);
        L[i] = {
          ...row,
          am: newAm,
          um: Number(um),
          am_k: newAmK,
          am_vat: newAmVat,
          am_supply: newAmK + newAmVat,
          um_k: um * rt_exch,
          um_supply: um * rt_exch + um_vat,
        };
        const key = keyOf(row);

        if (!groupMap.has(key)) {
          groupMap.set(key, {
            ...row,
            am: 0,
            um: 0,
            am_k: 0,
            am_vat: 0,
            am_supply: 0,
          });
        }

        const group = groupMap.get(key);

        group.am += newAm;
        group.um += Number(um);
        group.am_k += newAmK;
        group.am_vat += newAmVat;
        group.am_supply += newAmK + newAmVat;
      }
      let i = 0;
      groupMap.forEach((group) => {
        const row = H[i];
        H[i] = {
          ...row,
          am: group.am,
          um: group.um,
          am_k: group.am_k,
          am_vat: isNaN(group.am_vat) === true ? 0 : group.am_vat,
          am_supply: isNaN(group.am_supply) === true ? 0 : group.am_supply,
        };
        i++;
      });
    };
    const onAfterSetFooter = async () => {
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      const footerData = gridRefs.current["grid2"].current.getFooterData();
      if (focusedNodeH) {
        focusedNodeH.setDataValue("am", footerData.am);
        focusedNodeH.setDataValue("am_k", footerData.am_k);
        focusedNodeH.setDataValue("am_vat", footerData.am_vat);
        focusedNodeH.setDataValue("am_supply", footerData.am_supply);
      }
    };

    useImperativeHandle(
      ref,
      () => ({
        getSelectedValue: async () => {
          const closeFlag = await onSave();
          return { excelUpload: closeFlag };
        },
      }),
      [onSave]
    );
    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
          events: {
            onFocusedRowChanged: onFocusedRowChanged,
            onCellValueChanged: onCellValueChangedH,
          },
        },
        grid2: {
          columns: columns.grid2,
          dataSource: dataSources.grid2,
          events: {
            onCellValueChanged: onCellValueChangedL,
            onAfterSetFooter: onAfterSetFooter,
          },
        },
      }),
      [columns, dataSources]
    );
    return (
      <div style={{ position: "relative" }}>
        {/* 헤더 */}
        <div style={{ height: "32vh", marginTop: 10 }}>
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={headerColumns}
            rowData={[]}
            events={grids.grid1.events}
            etcProps={{
              rowSelection: "single",
              isEditable: true,
              popupParent: popupParent,
            }}
          />
        </div>

        {/* 디테일 */}
        <div style={{ height: "32vh", marginTop: 10 }}>
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={detailColumns}
            rowData={[]}
            events={grids.grid2.events}
            etcProps={{ rowSelection: "single", isEditable: true }}
          />
        </div>
      </div>
    );
  }
);
export default ExcelUpload_S02_ReviewSave;
